import { fetchJSON as fetchAPI } from './client';

export type BrandOpsDashboard = {
  tenant_id: string;
  registered_brands: number;
  active_trademarks: number;
  renewals_due: number;
  protected_assets: number;
  active_rules: number;
  compliance_violations: number;
  open_investigations: number;
  avg_health_score: number;
  avg_compliance_score: number;
  avg_consistency_score: number;
  active_recommendations: number;
  generated_at?: string;
};

export type BrandRecord = {
  id: string;
  brand_name: string;
  registry_status: string;
  brand_owner?: string;
  marketplaces?: string[];
  countries?: string[];
  trademark_count: number;
  health_score: number;
  compliance_score: number;
  consistency_score: number;
  protection_score: number;
  updated_at?: string;
};

export type TrademarkRecord = {
  id: string;
  brand_id?: string;
  trademark_number: string;
  trademark_class?: string;
  country?: string;
  status: string;
  registration_date?: string;
  renewal_date?: string;
  owner?: string;
  days_until_renewal?: number;
};

export type BrandRuleRecord = {
  id: string;
  brand_id?: string;
  scope: string;
  name: string;
  description?: string;
  rule_text?: string;
  is_active: boolean;
  priority: number;
  requires_approval: boolean;
  updated_at?: string;
};

export type ProtectedAssetRecord = {
  id: string;
  brand_id?: string;
  asset_type?: string;
  name: string;
  description?: string;
  is_protected: boolean;
  usage_count: number;
  updated_at?: string;
};

export type ComplianceEventRecord = {
  id: string;
  brand_id?: string;
  sku?: string;
  policy?: string;
  description?: string;
  status: string;
  severity?: string;
  detected_at?: string;
};

export type InvestigationRecord = {
  id: string;
  brand_id?: string;
  case_id: string;
  type?: string;
  status: string;
  description?: string;
  asin?: string;
  seller_id?: string;
  severity?: string;
  created_at?: string;
};

export type BrandHealthRecord = {
  id: string;
  brand_id: string;
  brand_name?: string;
  health_score: number;
  compliance_score: number;
  consistency_score: number;
  protection_score: number;
  policy_risk?: number;
  ai_alignment_score?: number;
  issue?: string;
  severity?: string;
};

export type BrandRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  brand_id?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_BRANDOPS_API ?? '/api/v1/brandops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const brandopsApi = {
  dashboard: () => fetchJSON<BrandOpsDashboard>('/dashboard'),
  listBrands: () => fetchJSON<BrandRecord[]>('/brands'),
  listTrademarks: () => fetchJSON<TrademarkRecord[]>('/trademarks'),
  listRules: () => fetchJSON<BrandRuleRecord[]>('/rules'),
  listAssets: () => fetchJSON<ProtectedAssetRecord[]>('/assets'),
  listCompliance: () => fetchJSON<ComplianceEventRecord[]>('/compliance'),
  listInvestigations: () => fetchJSON<InvestigationRecord[]>('/investigations'),
  listHealth: () => fetchJSON<BrandHealthRecord[]>('/health'),
  listRecommendations: () => fetchJSON<BrandRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<BrandRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
